import { useEffect, useRef, useState } from 'react';
import { api, setToken } from './api';
import type { AuthState, DomainDetail } from './types';
import { Login } from './pages/Login';
import { VerifyEmail } from './pages/VerifyEmail';
import { DomainList } from './pages/DomainList';
import { AddDomain } from './pages/AddDomain';
import { DomainDetailPage } from './pages/DomainDetail';
import { OnboardingPage } from './pages/onboarding';
import { UnifiedOnboardingWizard } from './pages/onboarding/UnifiedOnboardingWizard';
import { Sidebar } from './components/Sidebar';
import { GlossaryPage } from './pages/GlossaryPage';
import { AuditPage } from './pages/AuditPage';
import { AuditReportDetail } from './pages/AuditReportDetail';
import { saveReport } from './utils/reportStorage';
import type { AuditResponse } from './types';
import { AfiOverviewPage } from './pages/dashboard/OverviewPage';
import { AfiReviewQueuePage } from './pages/dashboard/ReviewQueuePage';
import { AfiActivityPage } from './pages/dashboard/ActivityPage';
import { AfiBrandVoicePage } from './pages/dashboard/BrandVoicePage';
import { AfiSettingsPage } from './pages/dashboard/SettingsPage';
import { AgencyDashboard } from './pages/AgencyDashboard';
import { AiTruthWizard } from './pages/ai-truth/AiTruthWizard';

type Screen =
  | { name: 'login' }
  | { name: 'verify-email'; token: string }
  | { name: 'reset-password'; token: string }
  | { name: 'glossary' }
  | { name: 'audit' }
  | { name: 'audit-result'; domain: string; data: AuditResponse; savedAt: string }
  | { name: 'list' }
  | { name: 'add' }
  | { name: 'unified-onboarding' }
  | { name: 'detail'; domainId: string; tab?: string }
  | { name: 'onboarding'; domainId: string }
  | { name: 'afi-overview'; domainId: string }
  | { name: 'afi-review'; domainId: string; tab?: string }
  | { name: 'afi-activity'; domainId: string }
  | { name: 'afi-brand-voice'; domainId: string }
  | { name: 'afi-settings'; domainId: string }
  | { name: 'agency-overview' }
  | { name: 'ai-truth-setup'; domainId: string; domain: DomainDetail };

const TOKEN_KEY = 'fixmyseo_access_token';
const EMAIL_KEY = 'fixmyseo_email';
const TIER_KEY  = 'fixmyseo_tier';
const PENDING_TIER_KEY = 'fixmyseo_pending_tier';

export function App() {
  const [auth, setAuth] = useState<AuthState | null>(null);
  const [redirecting, setRedirecting] = useState(false);

  // Check for special URL tokens before deciding initial screen
  const urlParams = new URLSearchParams(window.location.search);
  const resetToken = urlParams.get('reset-token') ?? (window.location.pathname === '/reset-password' ? urlParams.get('token') : null);
  const verifyToken = window.location.pathname === '/verify-email' ? urlParams.get('token') : null;
  const isSignupPath = window.location.pathname === '/signup';
  const isGlossaryPath = window.location.pathname === '/glossary';
  const isAuditPath = window.location.pathname === '/audit';

  // ?tier= or ?plan= from landing page plan buttons — store for post-login checkout redirect.
  // ?plan= is used by the pricing page CTAs (starter/pro/agency); normalise to billing names.
  // Use a ref so re-renders (e.g. triggered by setAuth) don't re-set the key after it's been removed.
  const PLAN_TO_BILLING: Record<string, 'starter' | 'growth' | 'scale'> = {
    starter: 'starter', tier_1: 'starter',
    pro: 'growth', growth: 'growth', tier_2: 'growth',
    agency: 'scale', scale: 'scale', tier_3: 'scale',
  };
  const PLAN_TO_TIER: Record<string, 'tier_1' | 'tier_2' | 'tier_3'> = {
    starter: 'tier_1', tier_1: 'tier_1',
    pro: 'tier_2', growth: 'tier_2', tier_2: 'tier_2',
    agency: 'tier_3', scale: 'tier_3', tier_3: 'tier_3',
  };
  const rawPlan = urlParams.get('tier') ?? urlParams.get('plan') ?? '';
  const signupTier = PLAN_TO_BILLING[rawPlan] ?? null;
  const signupTierSlug = PLAN_TO_TIER[rawPlan] ?? undefined;
  const pendingTierSaved = useRef(false);
  if (signupTierSlug && !pendingTierSaved.current) {
    pendingTierSaved.current = true;
    localStorage.setItem(PENDING_TIER_KEY, signupTierSlug);
  }

  const initialScreen: Screen = resetToken
    ? { name: 'reset-password', token: resetToken }
    : verifyToken
    ? { name: 'verify-email', token: verifyToken }
    : isGlossaryPath
    ? { name: 'glossary' }
    : isAuditPath
    ? { name: 'audit' }
    : { name: 'login' };

  const [screen, setScreen] = useState<Screen>(initialScreen);
  const [booting, setBooting] = useState(!resetToken && !verifyToken && !isGlossaryPath && !isAuditPath);

  // On mount: try to restore token from localStorage, then refresh it
  useEffect(() => {
    if (resetToken || verifyToken || isGlossaryPath || isAuditPath) return; // skip auto-login when handling a special link or public page

    // Safety net: clear pending tier if returning from Stripe billing pages
    if (window.location.pathname.startsWith('/billing/')) {
      localStorage.removeItem(PENDING_TIER_KEY);
    }

    const saved = localStorage.getItem(TOKEN_KEY);
    const isBillingSuccess = window.location.pathname === '/billing/success';
    const billingSessionId = isBillingSuccess
      ? new URLSearchParams(window.location.search).get('session_id')
      : null;
    if (saved) {
      setToken(saved);
      // Attempt silent refresh
      api.auth.refresh()
        .then(async (res) => {
          let tier = res.tier;
          let email = res.email;
          let accessToken = res.accessToken;

          // If returning from Stripe, resolve tier immediately from the session
          if (billingSessionId) {
            try {
              const session = await api.billing.resolveSession(billingSessionId);
              tier = session.tier;
              // Re-refresh so the access token carries the updated tier
              const refreshed = await api.auth.refresh();
              accessToken = refreshed.accessToken;
              tier = refreshed.tier;
              email = refreshed.email;
            } catch {
              // resolveSession failed (e.g. session already processed by webhook) — use current tier
            }
          }

          setToken(accessToken);
          localStorage.setItem(TOKEN_KEY, accessToken);
          localStorage.setItem(TIER_KEY, tier);
          localStorage.setItem(EMAIL_KEY, email);
          setAuth({ accessToken, email, tier });

          // If arriving from a landing plan button, redirect to Stripe checkout
          const pendingTier = localStorage.getItem(PENDING_TIER_KEY) as 'tier_1' | 'tier_2' | 'tier_3' | null;
          if (pendingTier && ['tier_1', 'tier_2', 'tier_3'].includes(pendingTier)) {
            localStorage.removeItem(PENDING_TIER_KEY);
            try {
              setRedirecting(true);
              const { url } = await api.billing.checkout(pendingTier);
              if (url) { window.location.href = url; return; }
            } catch {
              setRedirecting(false);
            }
          }

          // After Stripe checkout, route new users to unified onboarding.
          // Returning users (already have domains) go to their domain list.
          if (isBillingSuccess) {
            try {
              const existingDomains = await api.domains.list();
              if (existingDomains.length === 0) {
                setScreen({ name: 'unified-onboarding' });
              } else {
                setScreen(tier === 'tier_3' ? { name: 'agency-overview' } : { name: 'list' });
              }
            } catch {
              setScreen({ name: 'unified-onboarding' });
            }
          } else {
            setScreen(tier === 'tier_3' ? { name: 'agency-overview' } : { name: 'list' });
          }
        })
        .catch(() => {
          // Refresh token expired — force login
          localStorage.removeItem(TOKEN_KEY);
          setToken(null);
          setScreen({ name: 'login' });
        })
        .finally(() => setBooting(false));
    } else {
      setBooting(false);
      setScreen({ name: 'login' });
    }
  }, []);

  async function handleLogin(a: AuthState) {
    localStorage.setItem(TOKEN_KEY, a.accessToken);
    localStorage.setItem(EMAIL_KEY, a.email);
    localStorage.setItem(TIER_KEY, a.tier);

    // If a pending tier was saved from a landing page plan button, redirect to Stripe checkout
    const pendingTier = localStorage.getItem(PENDING_TIER_KEY) as 'tier_1' | 'tier_2' | 'tier_3' | null;
    if (pendingTier && ['tier_1', 'tier_2', 'tier_3'].includes(pendingTier)) {
      localStorage.removeItem(PENDING_TIER_KEY);
      try {
        setRedirecting(true);
        const { url } = await api.billing.checkout(pendingTier);
        if (url) { window.location.href = url; return; }
      } catch {
        setRedirecting(false);
        // Fall through to normal navigation if checkout fails
      }
    }

    // Set auth after checkout check to avoid triggering re-renders that could re-set PENDING_TIER_KEY
    setAuth(a);
    setScreen(a.tier === 'tier_3' ? { name: 'agency-overview' } : { name: 'list' });
  }

  function handleLogout() {
    api.auth.logout().catch(() => null);
    setToken(null);
    setAuth(null);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(EMAIL_KEY);
    localStorage.removeItem(TIER_KEY);
    setScreen({ name: 'login' });
  }

  function handleDomainCreated(_domain: DomainDetail) {
    setScreen({ name: 'list' });
  }

  if (booting || redirecting) {
    return (
      <div className="min-h-screen bg-brand-paper flex flex-col items-center justify-center gap-3">
        <div className="w-6 h-6 rounded-full border-2 border-brand-green border-t-transparent animate-spin" />
        {redirecting && <p className="text-sm text-brand-muted">Redirecting to checkout…</p>}
      </div>
    );
  }

  if (screen.name === 'verify-email') {
    return <VerifyEmail token={screen.token} onDone={() => setScreen({ name: 'login' })} />;
  }

  if (screen.name === 'glossary') {
    return <GlossaryPage onBack={() => setScreen({ name: 'login' })} />;
  }

  if (screen.name === 'audit') {
    return (
      <AuditPage
        onComplete={(data) => {
          const savedAt = new Date().toISOString();
          saveReport(data.domain, data);
          setScreen({ name: 'audit-result', domain: data.domain, data, savedAt });
        }}
      />
    );
  }

  if (screen.name === 'audit-result') {
    return (
      <AuditReportDetail
        data={screen.data}
        savedAt={screen.savedAt}
        domainId={screen.domain}
        onBack={() => setScreen({ name: 'audit' })}
      />
    );
  }

  if (screen.name === 'reset-password') {
    return (
      <Login
        onLogin={handleLogin}
        initialMode="reset-password"
        resetToken={screen.token}
        onResetDone={() => setScreen({ name: 'login' })}
      />
    );
  }

  if (screen.name === 'login' || !auth) {
    return (
      <Login
        onLogin={handleLogin}
        initialMode={isSignupPath || signupTier ? 'signup' : 'login'}
        {...(signupTierSlug !== undefined ? { signupTier: signupTierSlug } : {})}
      />
    );
  }

  // Determine sidebar "active screen" type
  type SidebarScreen = 'domains' | 'detail' | 'detail-audits' | 'detail-suggestions' | 'detail-content' | 'detail-activity' | 'detail-outreach' | 'add' | 'afi-overview' | 'afi-review' | 'afi-activity' | 'afi-brand-voice' | 'afi-settings' | 'agency-overview';
  const sidebarScreen: SidebarScreen =
    screen.name === 'add' ? 'add'
    : screen.name === 'detail' && screen.tab === 'analytics'   ? 'detail-audits'
    : screen.name === 'detail' && screen.tab === 'suggestions' ? 'detail-suggestions'
    : screen.name === 'detail' && screen.tab === 'content'     ? 'detail-content'
    : screen.name === 'detail' && screen.tab === 'activity'    ? 'detail-activity'
    : screen.name === 'detail' && screen.tab === 'outreach'   ? 'detail-outreach'
    : screen.name === 'detail' ? 'detail'
    : screen.name === 'afi-overview' ? 'afi-overview'
    : screen.name === 'afi-review' ? 'afi-review'
    : screen.name === 'afi-activity' ? 'afi-activity'
    : screen.name === 'afi-brand-voice' ? 'afi-brand-voice'
    : screen.name === 'afi-settings' ? 'afi-settings'
    : screen.name === 'agency-overview' ? 'agency-overview'
    : 'domains';

  const activeDomainId =
    screen.name === 'detail' ? screen.domainId
    : screen.name === 'onboarding' ? screen.domainId
    : screen.name === 'afi-overview' ? screen.domainId
    : screen.name === 'afi-review' ? screen.domainId
    : screen.name === 'afi-activity' ? screen.domainId
    : screen.name === 'afi-brand-voice' ? screen.domainId
    : screen.name === 'afi-settings' ? screen.domainId
    : screen.name === 'ai-truth-setup' ? screen.domainId
    : null;

  return (
    <div className="flex min-h-screen">
      {/* Skip to main content — first focusable element (WCAG 2.4.1) */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <div className="print:hidden">
        <Sidebar
        tier={auth.tier}
        email={auth.email}
        activeDomainId={activeDomainId}
        screen={sidebarScreen}
        onNavigate={(s) => {
          if (s === 'domains') setScreen(auth.tier === 'tier_3' ? { name: 'agency-overview' } : { name: 'list' });
          else if (s === 'add') setScreen({ name: 'add' });
          else if (s === 'detail' && activeDomainId)              setScreen({ name: 'detail', domainId: activeDomainId });
          else if (s === 'detail-audits' && activeDomainId)       setScreen({ name: 'detail', domainId: activeDomainId, tab: 'analytics' });
          else if (s === 'detail-suggestions' && activeDomainId)  setScreen({ name: 'detail', domainId: activeDomainId, tab: 'suggestions' });
          else if (s === 'detail-content' && activeDomainId)      setScreen({ name: 'detail', domainId: activeDomainId, tab: 'content' });
          else if (s === 'detail-activity' && activeDomainId)     setScreen({ name: 'detail', domainId: activeDomainId, tab: 'activity' });
          else if (s === 'detail-outreach' && activeDomainId)    setScreen({ name: 'detail', domainId: activeDomainId, tab: 'outreach' });
          else if (s === 'afi-overview' && activeDomainId)        setScreen({ name: 'afi-overview', domainId: activeDomainId });
          else if (s === 'afi-review' && activeDomainId)          setScreen({ name: 'afi-review', domainId: activeDomainId });
          else if (s === 'afi-activity' && activeDomainId)        setScreen({ name: 'afi-activity', domainId: activeDomainId });
          else if (s === 'afi-brand-voice' && activeDomainId)     setScreen({ name: 'afi-brand-voice', domainId: activeDomainId });
          else if (s === 'afi-settings' && activeDomainId)        setScreen({ name: 'afi-settings', domainId: activeDomainId });
          else if (s === 'agency-overview')                        setScreen({ name: 'agency-overview' });
        }}
        onLogout={handleLogout}
        onUpgrade={async () => {
          try {
            setRedirecting(true);
            const { url } = await api.billing.checkout('tier_1');
            if (url) window.location.href = url;
          } catch {
            setRedirecting(false);
          }
        }}
      />
      </div>
      <main id="main-content" tabIndex={-1} className="flex-1 overflow-y-auto bg-brand-paper print:w-full">
        {screen.name === 'unified-onboarding' && (
          <UnifiedOnboardingWizard
            onProceed={(domainId) => setScreen({ name: 'onboarding', domainId })}
          />
        )}
        {screen.name === 'add' && (
          <AddDomain
            onBack={() => setScreen({ name: 'list' })}
            onCreated={handleDomainCreated}
          />
        )}
        {screen.name === 'detail' && (
          <DomainDetailPage
            key={`${screen.domainId}-${screen.tab ?? 'overview'}`}
            domainId={screen.domainId}
            tier={auth.tier}
            {...(screen.tab !== undefined ? { initialTab: screen.tab } : {})}
            onBack={() => setScreen({ name: 'list' })}
            onStartOnboarding={() => setScreen({ name: 'onboarding', domainId: screen.domainId })}
            onAiTruthSetup={(d) =>
              setScreen({ name: 'ai-truth-setup', domainId: d.id, domain: d })
            }
          />
        )}
        {screen.name === 'list' && (
          <DomainList
            tier={auth.tier}
            onAddDomain={() => setScreen({ name: 'add' })}
            onSelectDomain={(id) => setScreen({ name: 'detail', domainId: id })}
            onStartOnboarding={(id) => setScreen({ name: 'onboarding', domainId: id })}
          />
        )}
        {screen.name === 'onboarding' && (
          <OnboardingPage
            domainId={screen.domainId}
            tier={auth.tier}
            onBack={() => setScreen({ name: 'detail', domainId: screen.domainId })}
          />
        )}
        {screen.name === 'afi-overview' && (
          <AfiOverviewPage
            domainId={screen.domainId}
            onNavigate={(name, tab?) => {
              const next: Screen =
                name === 'afi-review'
                  ? { name, domainId: screen.domainId, ...(tab !== undefined ? { tab } : {}) }
                  : { name, domainId: screen.domainId };
              setScreen(next);
            }}
          />
        )}
        {screen.name === 'afi-review' && (
          <AfiReviewQueuePage
            domainId={screen.domainId}
            {...(screen.tab !== undefined ? { initialTab: screen.tab } : {})}
            onBack={() => setScreen({ name: 'afi-overview', domainId: screen.domainId })}
          />
        )}
        {screen.name === 'afi-activity' && (
          <AfiActivityPage domainId={screen.domainId} />
        )}
        {screen.name === 'afi-brand-voice' && (
          <AfiBrandVoicePage domainId={screen.domainId} />
        )}
        {screen.name === 'afi-settings' && (
          <AfiSettingsPage
            domainId={screen.domainId}
            email={auth.email}
            tier={auth.tier}
            onBillingPortal={async () => {
              try {
                setRedirecting(true);
                const { url } = await api.billing.portal();
                if (url) window.location.href = url;
                else setRedirecting(false);
              } catch {
                setRedirecting(false);
                alert('Could not open billing portal — no Stripe customer record found for this account. Contact support if you believe this is an error.');
              }
            }}
          />
        )}
        {screen.name === 'agency-overview' && (
          <AgencyDashboard
            onSelectDomain={(id) => setScreen({ name: 'detail', domainId: id })}
            onSelectFixes={(id) => setScreen({ name: 'afi-review', domainId: id })}
          />
        )}
        {screen.name === 'ai-truth-setup' && (
          <AiTruthWizard
            domain={screen.domain}
            aiTruthEnabled={true}
            tier={auth.tier}
            onBack={() => setScreen({ name: 'detail', domainId: screen.domainId })}
          />
        )}
      </main>
    </div>
  );
}
